import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useLocale } from '../../context/LocaleContext';
import { sectionSlugForDashboardPath } from '../../nav/botNavRoutes';
import { reportDashboardUserNavigated } from '../../utils/botSessionBridge';
import {
  Mail,
  MessageCircle,
  UserCircle,
  Users,
  LineChart,
  Megaphone,
  BookOpen,
  Settings,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Hash,
  Radio,
  Sparkles,
} from 'lucide-react';

const BOT_ONLY = (process.env.REACT_APP_DASHBOARD_BOT_ONLY_NAV || '').trim() === '1';

/** Which collapsible nav group owns this path (for accordion + route sync). */
function sectionForPath(pathname) {
  if (pathname.startsWith('/inbox') || pathname.startsWith('/conversations')) return 'inboxConversations';
  if (pathname.startsWith('/bonnie')) return 'bonnie';
  if (pathname.startsWith('/reports')) return 'reports';
  if (pathname.startsWith('/campaigns')) return 'campaigns';
  if (pathname.startsWith('/help')) return 'help';
  if (pathname.startsWith('/settings')) return 'settings';
  return null;
}

/** First route in each sidebar group — used when opening a group from its header. */
const GROUP_FIRST_PATH = {
  inboxConversations: '/inbox',
  bonnie: '/bonnie/faqs',
  reports: '/reports/overview',
  campaigns: '/campaigns/live-chat',
  help: '/help/articles',
  settings: '/settings/account',
};

function NavRow({ to, section, icon: Icon, label, end }) {
  const iconEl = <Icon size={18} strokeWidth={1.75} className="custio-nav-group-toggle__icon" />;
  if (BOT_ONLY) {
    return (
      <button type="button" className="custio-nav-link custio-nav-link--child bot-disabled" data-section={section} disabled>
        {iconEl}
        <span>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10 }}>🔒</span>
      </button>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      data-section={section}
      className={({ isActive }) =>
        `custio-nav-link custio-nav-link--child${isActive ? ' custio-nav-link--active' : ''}`
      }
    >
      {iconEl}
      <span>{label}</span>
    </NavLink>
  );
}

function Group({ id, title, icon: Icon, open, onToggle, children }) {
  return (
    <div>
      <button
        type="button"
        className={`custio-nav-group-toggle${open ? ' custio-nav-group-toggle--open' : ''}`}
        onClick={() => onToggle(id)}
        aria-expanded={open}
      >
        {Icon ? (
          <Icon size={18} strokeWidth={1.75} className="custio-nav-group-toggle__icon" />
        ) : null}
        <span>{title}</span>
        <span className="custio-nav-group-toggle__chev">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
      </button>
      {open && <div className="custio-nav-sublinks">{children}</div>}
    </div>
  );
}

export default function SidebarNav() {
  const { t } = useLocale();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(() => sectionForPath(pathname));

  useEffect(() => {
    setOpenId(sectionForPath(pathname));
  }, [pathname]);

  /**
   * Accordion: opening a group closes others and navigates to that group's first page.
   * Clicking the open group's header collapses it (no navigation).
   */
  const toggle = (id) => {
    setOpenId((current) => {
      if (current === id) return null;
      const dest = GROUP_FIRST_PATH[id];
      if (dest && !BOT_ONLY) {
        const slug = sectionSlugForDashboardPath(dest);
        if (slug) reportDashboardUserNavigated(slug);
        navigate(dest);
      }
      return id;
    });
  };

  const topLink = (to, section, Icon, label) => {
    if (BOT_ONLY) {
      return (
        <button type="button" className="custio-nav-link bot-disabled" data-section={section} disabled>
          <Icon size={18} strokeWidth={1.75} className="custio-nav-group-toggle__icon" />
          <span>{label}</span>
          <span style={{ marginLeft: 'auto', fontSize: 10 }}>🔒</span>
        </button>
      );
    }
    return (
      <NavLink
        to={to}
        data-section={section}
        className={({ isActive }) => `custio-nav-link${isActive ? ' custio-nav-link--active' : ''}`}
      >
        <Icon size={18} strokeWidth={1.75} className="custio-nav-group-toggle__icon" />
        <span>{label}</span>
      </NavLink>
    );
  };

  return (
    <>
      <Group
        id="inboxConversations"
        title={t('nav.inboxConversations')}
        icon={Mail}
        open={openId === 'inboxConversations'}
        onToggle={toggle}
      >
        <NavRow to="/inbox" section="inbox" icon={Mail} label={t('nav.inbox')} end />
        <NavRow to="/conversations" section="all-conversations" icon={MessageCircle} label={t('nav.allConversations')} end />
        <NavRow to="/conversations/mentions" section="mentions" icon={Hash} label={t('nav.mentions')} />
        <NavRow to="/conversations/unattended" section="unattended" icon={Mail} label={t('nav.unattended')} />
      </Group>

      <Group id="bonnie" title={t('nav.bonnie')} icon={Sparkles} open={openId === 'bonnie'} onToggle={toggle}>
        <NavRow to="/bonnie/faqs" section="bonnie-faqs" icon={BookOpen} label={t('nav.faqs')} />
        <NavRow to="/bonnie/documents" section="bonnie-documents" icon={FolderOpen} label={t('nav.documents')} />
      </Group>

      {topLink('/contacts', 'contacts', Users, t('nav.contacts'))}

      <Group id="reports" title={t('nav.reports')} icon={LineChart} open={openId === 'reports'} onToggle={toggle}>
        <NavRow to="/reports/overview" section="dashboard" icon={LineChart} label={t('nav.overview')} end />
        <NavRow to="/reports/conversations" section="analytics" icon={MessageCircle} label={t('nav.conversations')} />
        <NavRow to="/reports/agents" section="reports-agents" icon={UserCircle} label={t('nav.agents')} />
      </Group>

      <Group id="campaigns" title={t('nav.campaigns')} icon={Megaphone} open={openId === 'campaigns'} onToggle={toggle}>
        <NavRow to="/campaigns/live-chat" section="campaigns-live" icon={Megaphone} label={t('nav.liveChat')} />
        <NavRow to="/campaigns" section="campaigns" icon={Mail} label={t('nav.allCampaigns')} end />
      </Group>

      <Group id="help" title={t('nav.helpCenter')} icon={BookOpen} open={openId === 'help'} onToggle={toggle}>
        <NavRow to="/help/articles" section="help-articles" icon={BookOpen} label={t('nav.articles')} />
        <NavRow to="/help/categories" section="help-categories" icon={FolderOpen} label={t('nav.categories')} />
        <NavRow to="/help/locales" section="help-locales" icon={Hash} label={t('nav.locales')} />
      </Group>

      <Group id="settings" title={t('nav.settings')} icon={Settings} open={openId === 'settings'} onToggle={toggle}>
        <NavRow to="/settings/account" section="settings" icon={Settings} label={t('nav.accountSettings')} />
        <NavRow to="/settings/agents" section="settings-agents" icon={Users} label={t('nav.agents')} />
        <NavRow to="/settings/teams" section="settings-teams" icon={Users} label={t('nav.teams')} />
        <NavRow to="/settings/inboxes" section="settings-inboxes" icon={Mail} label={t('nav.settingsInboxes')} />
        <NavRow to="/settings/labels" section="settings-labels" icon={Hash} label={t('nav.labels')} />
        <NavRow to="/settings/automation" section="settings-automation" icon={Sparkles} label={t('nav.automation')} />
        <NavRow to="/settings/canned-responses" section="settings-canned" icon={MessageCircle} label={t('nav.cannedResponses')} />
        <NavRow to="/settings/integrations" section="settings-integrations" icon={Radio} label={t('nav.integrations')} />
        <NavRow to="/settings/audit-logs" section="settings-audit" icon={LineChart} label={t('nav.auditLogs')} />
      </Group>
    </>
  );
}
